import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, Calendar, User, Phone, Mail, CreditCard, Bed, 
  Loader2, CheckCircle2, Home, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs } from '@/context/TabsContext';
import PhoneInput from '@/components/PhoneInput';
import AddressInput from '@/components/AddressInput';
import IdDocumentUpload from '@/components/IdDocumentUpload';

export default function NewBookingTab({ tabId, data, isEdit = false }) {
  const { closeTab } = useTabs();
  const { booking: editBooking, prefillGuest, onComplete } = data || {};
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [bedTypes, setBedTypes] = useState([]);
  const [idTypes, setIdTypes] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [searchingPhone, setSearchingPhone] = useState(false);
  const [hotelTaxSettings, setHotelTaxSettings] = useState({ room_tax: 12 });

  const [formData, setFormData] = useState({
    room_type_id: '',
    smoking_preference: 'no_preference',
    bed_type_id: '',
    number_of_rooms: 1,
    check_in: '',
    check_out: '',
    room_rate: '',
    discount: 0,
    advance_paid: 0,
    notes: '',
    existing_guest_id: null,
    primary_guest: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pin_code: '',
        country: 'India'
      },
      id_type: '',
      id_number: '',
      id_proof_image: ''
    },
    additional_guests: []
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isEdit && editBooking) {
      // Populate form with existing booking data
      setFormData(prev => ({
        ...prev,
        room_type_id: editBooking.room_type_id || '',
        room_id: editBooking.room_id || '',  // Track assigned room
        check_in: editBooking.check_in_date || editBooking.check_in || '',
        check_out: editBooking.check_out_date || editBooking.check_out || '',
        room_rate: editBooking.room_rate?.toString() || '',
        discount: editBooking.discount || 0,
        advance_paid: editBooking.advance_paid || 0,
        notes: editBooking.notes || '',
        existing_guest_id: editBooking.guest_id
      }));
      
      // Load guest data for editing
      if (editBooking.guest_id) {
        loadGuestData(editBooking.guest_id);
      }
      
      // Load room data if assigned
      if (editBooking.room_id) {
        loadRoomData(editBooking.room_id);
      }
    }
  }, [isEdit, editBooking]);

  // Handle pre-filled guest from Guest Management
  useEffect(() => {
    if (prefillGuest && !isEdit) {
      // Parse the guest name into first_name and last_name
      const nameParts = (prefillGuest.name || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Handle address - ensure it's an object
      const guestAddress = prefillGuest.address && typeof prefillGuest.address === 'object' 
        ? prefillGuest.address 
        : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' };
      
      setFormData(prev => ({
        ...prev,
        existing_guest_id: prefillGuest.id,
        primary_guest: {
          first_name: prefillGuest.first_name || firstName,
          last_name: prefillGuest.last_name || lastName,
          phone: prefillGuest.phone || '',
          email: prefillGuest.email || '',
          address: guestAddress,
          id_type: prefillGuest.id_type_name || prefillGuest.id_type_id || '',
          id_number: prefillGuest.id_number || '',
          id_proof_image: prefillGuest.id_proof_image || ''
        }
      }));
    }
  }, [prefillGuest, isEdit]);

  const loadGuestData = async (guestId) => {
    try {
      const res = await api.get(`/guests/${guestId}`);
      const guest = res.data;
      // Ensure address is an object, not null or string
      const guestAddress = guest.address && typeof guest.address === 'object' 
        ? guest.address 
        : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' };
      
      setFormData(prev => ({
        ...prev,
        primary_guest: {
          first_name: guest.first_name || guest.name?.split(' ')[0] || '',
          last_name: guest.last_name || guest.name?.split(' ').slice(1).join(' ') || '',
          phone: guest.phone || '',
          email: guest.email || '',
          address: guestAddress,
          id_type: guest.id_type_name || guest.id_type_id || '',
          id_number: guest.id_number || '',
          id_proof_image: guest.id_proof_image || ''
        }
      }));
    } catch (error) {
      console.error('Failed to load guest data:', error);
    }
  };

  const [assignedRoom, setAssignedRoom] = useState(null);
  
  const loadRoomData = async (roomId) => {
    try {
      const res = await api.get(`/rooms/${roomId}`);
      setAssignedRoom(res.data);
    } catch (error) {
      console.error('Failed to load room data:', error);
    }
  };

  const loadInitialData = async () => {
    try {
      const [roomTypesRes, bedTypesRes, idTypesRes, taxRes, pricingRes] = await Promise.all([
        api.get('/room-types'),
        api.get('/bed-types'),
        api.get('/id-types'),
        api.get('/saas/hotels/tax-settings').catch(() => ({ data: { tax_settings: { room_tax: 12 } } })),
        api.get('/room-pricing?active_only=true').catch(() => ({ data: [] }))
      ]);
      
      // Filter room types to only those that have active pricing rules
      const pricingRules = pricingRes.data || [];
      const pricedRoomTypeIds = new Set(pricingRules.filter(p => p.room_type_id).map(p => p.room_type_id));
      
      // If there are pricing rules with room types, only show those room types
      // Otherwise, show all room types (backward compatibility)
      let filteredRoomTypes = roomTypesRes.data;
      if (pricedRoomTypeIds.size > 0) {
        filteredRoomTypes = roomTypesRes.data.filter(rt => pricedRoomTypeIds.has(rt.id));
        
        // Enrich room types with pricing info from the pricing rules
        filteredRoomTypes = filteredRoomTypes.map(rt => {
          const pricing = pricingRules.find(p => p.room_type_id === rt.id);
          return {
            ...rt,
            base_price: pricing?.base_price || rt.base_price || 0
          };
        });
      }
      
      setRoomTypes(filteredRoomTypes);
      setBedTypes(bedTypesRes.data.filter(b => b.is_active));
      setIdTypes(idTypesRes.data);
      if (taxRes.data?.tax_settings) {
        setHotelTaxSettings(taxRes.data.tax_settings);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updatePrimaryGuest = (field, value) => {
    setFormData(prev => ({
      ...prev,
      primary_guest: { ...prev.primary_guest, [field]: value }
    }));
  };

  const calculateNights = () => {
    if (!formData.check_in || !formData.check_out) return 0;
    const checkIn = new Date(formData.check_in);
    const checkOut = new Date(formData.check_out);
    const diff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const getSelectedRoomRate = () => {
    if (formData.room_rate) return parseFloat(formData.room_rate);
    const roomType = roomTypes.find(rt => rt.id === formData.room_type_id);
    return roomType ? roomType.base_price : 0;
  };

  const calculateEstimate = () => {
    const nights = calculateNights();
    const rate = getSelectedRoomRate();
    const subtotal = nights * rate;
    const discount = parseFloat(formData.discount || 0);
    const taxRate = (hotelTaxSettings?.room_tax || 12) / 100;
    const tax = (subtotal - discount) * taxRate;
    return { nights, rate, subtotal, discount, tax, total: subtotal - discount + tax, taxRate: hotelTaxSettings?.room_tax || 12 };
  };

  const checkAvailability = async () => {
    if (!formData.check_in || !formData.check_out || !formData.room_type_id) return;
    
    setCheckingAvailability(true);
    try {
      const params = new URLSearchParams({
        check_in: new Date(formData.check_in).toISOString(),
        check_out: new Date(formData.check_out).toISOString(),
        room_type_id: formData.room_type_id,
        number_of_rooms: formData.number_of_rooms.toString()
      });
      
      if (formData.smoking_preference && formData.smoking_preference !== 'no_preference') {
        params.append('smoking_preference', formData.smoking_preference);
      }
      if (formData.bed_type_id && formData.bed_type_id !== 'none') {
        params.append('bed_type_id', formData.bed_type_id);
      }
      
      const res = await api.get(`/rooms/availability?${params.toString()}`);
      // Transform API response to match frontend expectations
      const availabilityData = {
        available: res.data.can_book && res.data.total_available > 0,
        available_count: res.data.total_available,
        rooms: res.data.availability_by_type?.flatMap(type => type.rooms) || []
      };
      setAvailability(availabilityData);
      
      const selectedType = roomTypes.find(rt => rt.id === formData.room_type_id);
      if (selectedType && !formData.room_rate) {
        setFormData(prev => ({ ...prev, room_rate: selectedType.base_price.toString() }));
      }
    } catch (error) {
      toast.error('Failed to check availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const searchGuestByPhone = async (phone) => {
    if (!phone || phone.length < 10) return;
    
    setSearchingPhone(true);
    try {
      // Use the correct endpoint with phone as path parameter
      const res = await api.get(`/guests/search/phone/${encodeURIComponent(phone)}`);
      // Backend returns {found: boolean, guest: object|null}
      if (res.data && res.data.found && res.data.guest) {
        const guest = res.data.guest;
        // Ensure address is an object, not null or string
        const guestAddress = guest.address && typeof guest.address === 'object' 
          ? guest.address 
          : { address_line1: '', address_line2: '', city: '', state: '', pin_code: '', country: 'India' };
        
        setFormData(prev => ({
          ...prev,
          existing_guest_id: guest.id,
          primary_guest: {
            ...prev.primary_guest,
            first_name: guest.first_name || guest.name?.split(' ')[0] || '',
            last_name: guest.last_name || guest.name?.split(' ').slice(1).join(' ') || '',
            email: guest.email || '',
            address: guestAddress,
            id_type: guest.id_type_name || '',
            id_number: guest.id_number || '',
            phone: phone
          }
        }));
        toast.success('Guest found! Details auto-filled.');
      }
    } catch (error) {
      // Guest not found - that's okay
    } finally {
      setSearchingPhone(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.check_in || !formData.check_out) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    
    if (!formData.room_type_id) {
      toast.error('Please select a room type');
      return;
    }
    
    if (!formData.existing_guest_id && !formData.primary_guest.phone) {
      toast.error('Please enter guest phone number');
      return;
    }
    
    if (!formData.existing_guest_id && !formData.primary_guest.first_name) {
      toast.error('Please enter guest first name');
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        room_type_id: formData.room_type_id,
        check_in: formData.check_in,
        check_out: formData.check_out,
        room_rate: parseFloat(formData.room_rate) || getSelectedRoomRate(),
        discount: parseFloat(formData.discount) || 0,
        advance_paid: parseFloat(formData.advance_paid) || 0,
        notes: formData.notes,
        smoking_preference: formData.smoking_preference,
        bed_type_id: formData.bed_type_id !== 'none' ? formData.bed_type_id : null,
        number_of_rooms: formData.number_of_rooms
      };

      if (formData.existing_guest_id) {
        bookingData.guest_id = formData.existing_guest_id;
      } else {
        bookingData.primary_guest = formData.primary_guest;
      }

      if (isEdit && editBooking) {
        // Always update guest info when editing
        if (formData.existing_guest_id) {
          try {
            const guestUpdateData = {
              name: `${formData.primary_guest.first_name || ''} ${formData.primary_guest.last_name || ''}`.trim(),
              phone: formData.primary_guest.phone || undefined
            };
            // Only include email if it has a value
            if (formData.primary_guest.email) {
              guestUpdateData.email = formData.primary_guest.email;
            }
            await api.put(`/guests/${formData.existing_guest_id}`, guestUpdateData);
          } catch (guestError) {
            console.error('Failed to update guest:', guestError);
            // Don't fail the booking update if guest update fails
          }
        }
        
        await api.patch(`/bookings/${editBooking.id}`, bookingData);
        toast.success('Booking updated successfully!');
      } else {
        await api.post('/bookings', bookingData);
        toast.success('Booking created successfully!');
      }
      
      onComplete?.();
      closeTab(tabId);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save booking'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  const estimate = calculateEstimate();

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            {isEdit ? <CreditCard className="h-6 w-6 text-blue-600" /> : <Plus className="h-6 w-6 text-orange-600" />}
            {isEdit ? 'Edit Booking' : 'New Booking'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEdit ? 'Update booking details' : 'Create a new room reservation'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-hidden">
        <div className="grid grid-cols-3 gap-6 h-full">
          {/* Left Column - Dates & Room */}
          <ScrollArea className="pr-4">
            <div className="space-y-4 pb-4">
              {/* Dates Section */}
              <Card className="border-orange-200">
                <CardHeader className="py-3 px-4 bg-orange-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-orange-700">
                    <Calendar className="h-4 w-4" />
                    Stay Dates
                    {estimate.nights > 0 && (
                      <Badge className="bg-orange-100 text-orange-700 ml-2">{estimate.nights} Night(s)</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Check-in Date *</Label>
                      <Input
                        type="date"
                        value={formData.check_in ? formData.check_in.slice(0, 10) : ''}
                        onChange={(e) => {
                          setFormData({ ...formData, check_in: e.target.value });
                          setAvailability(null);
                        }}
                        min={new Date().toISOString().slice(0, 10)}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Check-out Date *</Label>
                      <Input
                        type="date"
                        value={formData.check_out ? formData.check_out.slice(0, 10) : ''}
                        onChange={(e) => {
                          setFormData({ ...formData, check_out: e.target.value });
                          setAvailability(null);
                        }}
                        min={formData.check_in || new Date().toISOString().slice(0, 10)}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Room Selection */}
              <Card className="border-blue-200">
                <CardHeader className="py-3 px-4 bg-blue-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-blue-700">
                    <Home className="h-4 w-4" />
                    Room Preferences
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <Label className="text-xs text-gray-500">Room Type *</Label>
                    <Select 
                      value={formData.room_type_id} 
                      onValueChange={(v) => {
                        setFormData({ ...formData, room_type_id: v });
                        setAvailability(null);
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select room type" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomTypes.map(rt => (
                          <SelectItem key={rt.id} value={rt.id}>
                            {rt.name} - ₹{rt.base_price}/night
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Bed Type</Label>
                      <Select 
                        value={formData.bed_type_id || 'none'} 
                        onValueChange={(v) => setFormData({ ...formData, bed_type_id: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Any</SelectItem>
                          {bedTypes.map(bt => (
                            <SelectItem key={bt.id} value={bt.id}>{bt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Smoking</Label>
                      <Select 
                        value={formData.smoking_preference} 
                        onValueChange={(v) => setFormData({ ...formData, smoking_preference: v })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no_preference">No Preference</SelectItem>
                          <SelectItem value="non_smoking">Non-Smoking</SelectItem>
                          <SelectItem value="smoking">Smoking</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={checkAvailability}
                    disabled={!formData.check_in || !formData.check_out || !formData.room_type_id || checkingAvailability}
                    className="w-full"
                  >
                    {checkingAvailability ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking...</>
                    ) : (
                      'Check Availability'
                    )}
                  </Button>

                  {availability && (
                    <div className={`p-3 rounded-lg ${availability.available ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      {availability.available ? (
                        <p className="text-green-700 text-sm">
                          <CheckCircle2 className="h-4 w-4 inline mr-1" />
                          {availability.available_count} room(s) available
                        </p>
                      ) : (
                        <p className="text-red-700 text-sm">No rooms available for selected dates</p>
                      )}
                    </div>
                  )}
                  
                  {/* Show assigned room in edit mode */}
                  {isEdit && assignedRoom && (
                    <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                      <p className="text-indigo-700 text-sm font-medium flex items-center gap-2">
                        <Bed className="h-4 w-4" />
                        Assigned Room: {assignedRoom.room_number}
                      </p>
                      <p className="text-xs text-indigo-600 mt-1">
                        {assignedRoom.room_type || assignedRoom.type_name} • Floor {assignedRoom.floor || 'N/A'}
                      </p>
                    </div>
                  )}
                  
                  {isEdit && !assignedRoom && editBooking?.room_id && (
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-gray-600 text-sm">Loading room info...</p>
                    </div>
                  )}
                  
                  {isEdit && !editBooking?.room_id && (
                    <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                      <p className="text-yellow-700 text-sm">No room assigned yet. Room will be assigned at check-in.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pricing */}
              <Card>
                <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                  <CardTitle className="text-sm font-medium">Pricing</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Room Rate/Night</Label>
                      <Input
                        type="number"
                        value={formData.room_rate}
                        onChange={(e) => setFormData({ ...formData, room_rate: e.target.value })}
                        placeholder={getSelectedRoomRate().toString()}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Discount</Label>
                      <Input
                        type="number"
                        value={formData.discount}
                        onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Advance Payment</Label>
                    <Input
                      type="number"
                      value={formData.advance_paid}
                      onChange={(e) => setFormData({ ...formData, advance_paid: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>

          {/* Middle Column - Guest Details */}
          <ScrollArea className="pr-4">
            <div className="space-y-4 pb-4">
              <Card className="border-cyan-200">
                <CardHeader className="py-3 px-4 bg-cyan-50 border-b">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-cyan-700">
                    <User className="h-4 w-4" />
                    Primary Guest
                    {formData.existing_guest_id && (
                      <Badge className="bg-green-100 text-green-700 ml-2">Existing Guest</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div>
                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Mobile Number *
                    </Label>
                    <div className="relative mt-1">
                      <PhoneInput
                        value={formData.primary_guest.phone}
                        onChange={(value) => {
                          updatePrimaryGuest('phone', value);
                          if (value && value.length >= 10) {
                            searchGuestByPhone(value);
                          }
                        }}
                        placeholder="98765 43210"
                        required={!formData.existing_guest_id}
                      />
                      {searchingPhone && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Enter phone to auto-fill existing guest</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">First Name *</Label>
                      <Input
                        value={formData.primary_guest.first_name}
                        onChange={(e) => updatePrimaryGuest('first_name', e.target.value)}
                        placeholder="First name"
                        required={!formData.existing_guest_id}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Last Name</Label>
                      <Input
                        value={formData.primary_guest.last_name}
                        onChange={(e) => updatePrimaryGuest('last_name', e.target.value)}
                        placeholder="Last name"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={formData.primary_guest.email}
                      onChange={(e) => updatePrimaryGuest('email', e.target.value)}
                      placeholder="email@example.com"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-gray-500">Address</Label>
                    <AddressInput
                      value={formData.primary_guest.address}
                      onChange={(value) => updatePrimaryGuest('address', value)}
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">ID Type</Label>
                      <Select 
                        value={formData.primary_guest.id_type} 
                        onValueChange={(v) => updatePrimaryGuest('id_type', v)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select ID" />
                        </SelectTrigger>
                        <SelectContent>
                          {idTypes.length > 0 ? (
                            idTypes.map(type => (
                              <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                            ))
                          ) : (
                            <>
                              <SelectItem value="Aadhaar">Aadhaar Card</SelectItem>
                              <SelectItem value="Passport">Passport</SelectItem>
                              <SelectItem value="Driving License">Driving License</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">ID Number</Label>
                      <Input
                        value={formData.primary_guest.id_number}
                        onChange={(e) => updatePrimaryGuest('id_number', e.target.value)}
                        placeholder="ID number"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <IdDocumentUpload
                    value={formData.primary_guest.id_proof_image}
                    onChange={(url) => updatePrimaryGuest('id_proof_image', url)}
                    label="ID Document Photo"
                    guestId={formData.existing_guest_id}
                  />
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardContent className="p-4">
                  <Label className="text-xs text-gray-500">Booking Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any special requests or notes..."
                    className="mt-1 h-24"
                  />
                </CardContent>
              </Card>
            </div>
          </ScrollArea>

          {/* Right Column - Summary */}
          <div className="flex flex-col">
            <Card className="flex-1 flex flex-col">
              <CardHeader className="py-3 px-4 bg-gradient-to-r from-orange-50 to-purple-50 border-b">
                <CardTitle className="text-sm font-medium">Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex-1 flex flex-col">
                <div className="space-y-3 flex-1">
                  {formData.room_type_id && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Room Type</span>
                      <span className="font-medium">{roomTypes.find(rt => rt.id === formData.room_type_id)?.name}</span>
                    </div>
                  )}
                  {estimate.nights > 0 && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Duration</span>
                        <span className="font-medium">{estimate.nights} Night(s)</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Rate/Night</span>
                        <span className="font-medium">₹{estimate.rate}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium">₹{estimate.subtotal}</span>
                      </div>
                      {estimate.discount > 0 && (
                        <div className="flex justify-between text-sm text-red-600">
                          <span>Discount</span>
                          <span>- ₹{estimate.discount}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax ({estimate.taxRate}%)</span>
                        <span className="font-medium">₹{estimate.tax.toFixed(0)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between text-lg">
                        <span className="font-bold">Total</span>
                        <span className="font-bold text-orange-600">₹{estimate.total.toFixed(0)}</span>
                      </div>
                      {parseFloat(formData.advance_paid) > 0 && (
                        <>
                          <div className="flex justify-between text-sm text-emerald-600">
                            <span>Advance</span>
                            <span>- ₹{formData.advance_paid}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Balance</span>
                            <span className="font-medium">₹{(estimate.total - parseFloat(formData.advance_paid || 0)).toFixed(0)}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-2 pt-4 border-t mt-auto">
                  <Button
                    type="submit"
                    disabled={submitting || (!isEdit && !availability?.available)}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white h-11"
                    data-testid="save-booking-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                    ) : (
                      <><CheckCircle2 className="h-5 w-5 mr-2" /> {isEdit ? 'Update Booking' : 'Create Booking'}</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => closeTab(tabId)}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
